/**
 * Componente ScrollArea customizado com suporte a anti-scroll-chaining e controle de touch
 * Versão: 1.7.0 - Scrollbar horizontal, barra sempre visível e rolagem por wheel/touchpad
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-19 v1.8.0 - Wheel respeita roláveis internos (listas dentro de modais rolam
 *            primeiro e, no limite, a rolagem passa ao ancestral); viewport só intercepta quando
 *            consegue rolar na direção do gesto, evitando rolagem dupla.
 *   RELEASE: 2026-08-28 v1.7.0 - (1) Passa a renderizar TAMBÉM a ScrollBar horizontal
 *            (antes só existia a vertical, deixando áreas com scroll em X sem indicador).
 *            (2) Nova prop `alwaysVisibleScrollbar` (type="always" do Radix) para deixar a
 *            barra sempre visível quando há conteúdo excedente. (3) Handler de wheel que
 *            aplica a rolagem manualmente no viewport quando há overflow no eixo do gesto —
 *            resolve o caso de ScrollAreas dentro de Popover/Dialog do Radix, onde o bloqueio
 *            de scroll da camada impedia o touchpad/roda do mouse de rolar o conteúdo.
 *   RELEASE: 2026-08-12 v1.6.0 - Nova prop `fluidContent`: força o wrapper interno do Radix
 *            (que é renderizado com `display: table; min-width: 100%`) a se comportar como
 *            bloco de largura 100%. Sem isso, o conteúdo dimensiona pela largura do texto e
 *            classes `truncate`/`line-clamp` nunca são acionadas (cards vazam a borda).
 *            Ativado automaticamente quando axis='y' (scroll só vertical nunca deve crescer
 *            horizontalmente), mantendo retrocompatibilidade para axis='both'/'x'.
 *   RELEASE: v1.5.0 - reserveScrollbarSpace
 *   RELEASE: v1.4.0 - axis
 *   RELEASE: v1.3.0 - touchAxis
 *
 * @prop preventScrollChaining - Quando true, impede que o scroll "vaze" para
 *   containers ancestrais ao atingir o topo/rodapé. Útil para Kanban, listas, etc.
 * @prop touchAxis - Controla o eixo de pan em dispositivos touch:
 *   'auto' (padrão), 'pan-y' (vertical), 'pan-x' (horizontal)
 * @prop fluidContent - Quando true, o conteúdo respeita a largura do container
 * @prop alwaysVisibleScrollbar - Quando true, a barra fica sempre visível (type="always")
 */


import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

import { cn } from "@/lib/utils";

// v1.3.0: Mapeamento de touchAxis para classes Tailwind
const touchAxisClasses: Record<'auto' | 'pan-y' | 'pan-x', string> = {
  'auto': 'touch-auto',
  'pan-y': 'touch-pan-y',
  'pan-x': 'touch-pan-x',
};

// v1.4.0: Mapeamento de axis para classes de overflow
const axisOverflowClasses: Record<'both' | 'y' | 'x', string> = {
  'both': 'overflow-auto',
  'y': 'overflow-y-auto overflow-x-hidden',
  'x': 'overflow-x-auto overflow-y-hidden',
};

// Props estendidas para suportar anti-scroll-chaining, controle de touch e eixo de scroll
interface ScrollAreaProps extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  /**
   * Quando true, aplica overscroll-contain no Viewport para impedir
   * que o scroll "escape" para ancestrais (scroll chaining)
   */
  preventScrollChaining?: boolean;
  /**
   * v1.3.0: Controla o eixo de pan permitido em dispositivos touch
   * - 'auto': permite scroll em ambos os eixos (padrão)
   * - 'pan-y': otimizado para scroll vertical (ex: colunas do Kanban)
   * - 'pan-x': otimizado para scroll horizontal (ex: board do Kanban)
   */
  touchAxis?: 'auto' | 'pan-y' | 'pan-x';
  /**
   * v1.4.0: Controla o eixo de scroll permitido no container
   * - 'both': permite scroll em ambos os eixos (padrão, retrocompatível)
   * - 'y': apenas scroll vertical (útil para modais onde tabela controla scroll X)
   * - 'x': apenas scroll horizontal
   */
  axis?: 'both' | 'y' | 'x';
  /**
   * v1.5.0: Reserva espaço para a scrollbar evitando sobreposição de conteúdo
   * Quando true, aplica padding no viewport para que a scrollbar não cubra o conteúdo.
   * - Se axis='y' ou 'both': aplica padding-right (pr-3)
   * - Se axis='x' ou 'both': aplica padding-bottom (pb-3)
   * Útil para Kanbans e listas onde badges/ícones ficam na borda direita.
   */
  reserveScrollbarSpace?: boolean;
  /**
   * v1.6.0: Faz o conteúdo respeitar a largura do container.
   * O Radix renderiza um wrapper interno com `display: table; min-width: 100%`,
   * o que faz o conteúdo dimensionar pelo texto (quebrando `truncate`/`line-clamp`).
   * Quando true, esse wrapper vira `display: block; width: 100%; min-width: 0`.
   * Padrão: true quando axis='y' (scroll apenas vertical), false nos demais eixos.
   */
  fluidContent?: boolean;
  /**
   * v1.7.0: Mantém a barra de rolagem sempre visível (type="always" do Radix).
   * Útil quando é importante sinalizar que existe mais conteúdo (ex.: faixa
   * horizontal de categorias do seletor de emojis).
   */
  alwaysVisibleScrollbar?: boolean;
}

// v1.5.0: Mapeamento de reserveScrollbarSpace para classes de padding
// Reserva espaço para a scrollbar evitando que ela sobreponha o conteúdo
const getScrollbarSpaceClasses = (axis: 'both' | 'y' | 'x', reserve: boolean): string => {
  if (!reserve) return '';
  // Largura da scrollbar vertical = w-2.5 (~10px) + padding, arredondamos para pr-3 (~12px)
  // Altura da scrollbar horizontal = h-2.5 (~10px) + padding, arredondamos para pb-3 (~12px)
  if (axis === 'y') return 'pr-3';
  if (axis === 'x') return 'pb-3';
  return 'pr-3 pb-3'; // 'both'
};

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(({ className, children, preventScrollChaining = false, touchAxis = 'auto', axis = 'both', reserveScrollbarSpace = false, fluidContent, alwaysVisibleScrollbar = false, type, ...props }, ref) => {
  // v1.6.0: por padrão, scroll apenas vertical não deve crescer horizontalmente
  const applyFluidContent = fluidContent ?? axis === 'y';

  // v1.7.0: referência ao viewport para tratar o wheel de forma nativa (não-passiva)
  const viewportRef = React.useRef<HTMLDivElement | null>(null);

  /**
   * v1.7.0: Rolagem manual por wheel/touchpad.
   *
   * Motivo: dentro de camadas Radix (Popover/Dialog) o scroll do ponteiro pode ser
   * bloqueado pela camada, fazendo com que a área só role arrastando a barra.
   * Aqui aplicamos o delta diretamente no viewport quando existe overflow no eixo
   * do gesto, e impedimos o comportamento padrão apenas nesse caso.
   *
   * Observação: o listener é adicionado nativamente com `{ passive: false }` porque
   * o React registra `onWheel` de forma passiva (preventDefault não funcionaria).
   */
  React.useEffect(() => {
    const node = viewportRef.current;
    if (!node) return;

    const handleWheel = (event: WheelEvent) => {
      // Normaliza o delta (modo linha/página → pixels aproximados)
      const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? node.clientHeight : 1;
      const deltaY = event.deltaY * factor;
      const deltaX = event.deltaX * factor;

      // v1.8.0 — Pode rolar o elemento `el` na direção do gesto (sem estar no limite)?
      const canMove = (el: HTMLElement, d: number, vertical: boolean) => {
        if (d === 0) return false;
        if (vertical) {
          const max = el.scrollHeight - el.clientHeight;
          if (max <= 1) return false;
          return d < 0 ? el.scrollTop > 0 : el.scrollTop < max - 1;
        }
        const max = el.scrollWidth - el.clientWidth;
        if (max <= 1) return false;
        return d < 0 ? el.scrollLeft > 0 : el.scrollLeft < max - 1;
      };

      // Gesto majoritariamente horizontal (trackpad) ou shift+wheel
      const horizontalIntent = Math.abs(deltaX) > Math.abs(deltaY) || event.shiftKey;
      const vertical = !horizontalIntent;
      const delta = vertical ? deltaY : (Math.abs(deltaX) > 0 ? deltaX : deltaY);

      /**
       * v1.8.0 — Respeita rolagens internas: se entre o alvo e este viewport existir
       * um elemento rolável que ainda pode se mover na direção do gesto, não interferimos
       * (o navegador rola a lista interna). Antes, o viewport externo "roubava" o giro.
       */
      let el = event.target as HTMLElement | null;
      while (el && el !== node) {
        const st = window.getComputedStyle(el);
        const ov = vertical ? st.overflowY : st.overflowX;
        if ((ov === 'auto' || ov === 'scroll') && canMove(el, delta, vertical)) return;
        el = el.parentElement;
      }

      const allowed = vertical ? axis !== 'x' : axis !== 'y';
      if (allowed && canMove(node, delta, vertical)) {
        if (vertical) node.scrollTop += delta; else node.scrollLeft += delta;
        event.preventDefault();
        // Impede que ancestrais (outra ScrollArea) rolem junto
        event.stopPropagation();
        return;
      }

      // v1.8.0 — No limite (topo/fim): segue para os ancestrais, salvo preventScrollChaining
      const hasOverflow = vertical
        ? node.scrollHeight > node.clientHeight + 1
        : node.scrollWidth > node.clientWidth + 1;
      if (preventScrollChaining && allowed && hasOverflow) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => node.removeEventListener('wheel', handleWheel);
  }, [axis, preventScrollChaining]);

  return (
  <ScrollAreaPrimitive.Root
    ref={ref}
    // v1.7.0: `always` mantém a barra visível; caso contrário respeita o type informado
    type={type ?? (alwaysVisibleScrollbar ? 'always' : undefined)}
    className={cn("relative overflow-hidden min-h-0", className)}
    {...props}
  >
    <ScrollAreaPrimitive.Viewport 
      ref={viewportRef}
      className={cn(
        "h-full w-full rounded-[inherit]",
        // v1.6.0: neutraliza o wrapper interno `display:table` do Radix
        applyFluidContent && "[&>div]:!block [&>div]:!w-full [&>div]:!min-w-0",
        // v1.4.0: Controle de overflow por eixo (substitui overflow-auto fixo)
        axisOverflowClasses[axis],
        // v1.3.0: Aplica classe de touch baseada na prop touchAxis
        touchAxisClasses[touchAxis],
        // v1.2.0: Separação de overscroll por eixo para Kanban
        preventScrollChaining && "overscroll-y-contain overscroll-x-auto",
        // v1.5.0: Reserva espaço para scrollbar (evita sobreposição de conteúdo)
        getScrollbarSpaceClasses(axis, reserveScrollbarSpace)
      )}
    >
      {children}
    </ScrollAreaPrimitive.Viewport>
    {/* v1.7.0: barras vertical e horizontal conforme o eixo habilitado */}
    {axis !== 'x' && <ScrollBar orientation="vertical" />}
    {axis !== 'y' && <ScrollBar orientation="horizontal" />}
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
  );
});

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
